import { FormEvent, useEffect, useState } from 'react';
import { PlanningDeleteImpact } from '../../services/strategicPlanningApi';
import { ModalPortalShell } from '../ui/ModalPortalShell';

export type PlanningDeleteTransferOption = {
  id: string;
  label: string;
};

type PlanningDeleteConfirmModalProps = {
  open: boolean;
  title: string;
  itemLabel: string;
  impact: PlanningDeleteImpact | null;
  transferOptions: PlanningDeleteTransferOption[];
  transferLabel: string;
  onClose: () => void;
  onConfirm: (transferChildrenToId?: string) => Promise<void>;
};

export function PlanningDeleteConfirmModal({
  open,
  title,
  itemLabel,
  impact,
  transferOptions,
  transferLabel,
  onClose,
  onConfirm,
}: PlanningDeleteConfirmModalProps) {
  const [mode, setMode] = useState<'transfer' | 'cascade'>('transfer');
  const [transferToId, setTransferToId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode('transfer');
    setTransferToId('');
    setSaving(false);
  }, [open]);

  if (!open) return null;

  const requiresDecision = impact?.requiresTransferDecision ?? false;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      await onConfirm(mode === 'transfer' ? transferToId || undefined : undefined);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortalShell open={open}>
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={(event) => void handleSubmit(event)}>
        <div className="shrink-0 border-b border-secondary-200 px-5 py-4">
          <h2 className="erp-card-title">{title}</h2>
          <p className="erp-card-subtitle">
            {requiresDecision
              ? `"${itemLabel}" possui ${impact?.totalDescendantsCount ?? 0} item(ns) vinculado(s). Escolha como tratar os filhos.`
              : `Confirma a exclusão de "${itemLabel}"?`}
          </p>
        </div>

        <div className="erp-card-body min-h-0 flex-1 space-y-4 overflow-y-auto">
          {requiresDecision ? (
            <div className="space-y-3">
              <label className="flex items-start gap-2 rounded-lg border border-secondary-200 p-3">
                <input
                  type="radio"
                  name="delete-mode"
                  checked={mode === 'transfer'}
                  onChange={() => setMode('transfer')}
                />
                <span>
                  <span className="block text-sm font-medium text-secondary-900">
                    Mover filhos para outro item
                  </span>
                  <span className="text-xs text-secondary-500">
                    Os itens vinculados serão realocados antes da exclusão.
                  </span>
                </span>
              </label>

              {mode === 'transfer' ? (
                <label className="block pl-7">
                  <span className="erp-label">{transferLabel}</span>
                  <select
                    className="erp-input"
                    value={transferToId}
                    onChange={(event) => setTransferToId(event.target.value)}
                    required
                  >
                    <option value="">Selecione o destino</option>
                    {transferOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <label className="flex items-start gap-2 rounded-lg border border-secondary-200 p-3">
                <input
                  type="radio"
                  name="delete-mode"
                  checked={mode === 'cascade'}
                  onChange={() => setMode('cascade')}
                />
                <span>
                  <span className="block text-sm font-medium text-secondary-900">
                    Excluir em cascata
                  </span>
                  <span className="text-xs text-secondary-500">
                    Todos os itens vinculados serão excluídos junto com este registro.
                  </span>
                </span>
              </label>
            </div>
          ) : null}
        </div>

        <div className="erp-btn-actions shrink-0 border-t border-secondary-200 bg-white px-5 py-4">
          <button type="button" className="erp-btn-secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button
            type="submit"
            className="erp-btn-danger"
            disabled={saving || (mode === 'transfer' && requiresDecision && !transferToId)}
          >
            {saving ? 'Excluindo…' : 'Excluir'}
          </button>
        </div>
      </form>
    </ModalPortalShell>
  );
}
